import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { MethodsHelper } from '@app/helpers/methods.helper';
import { Branch } from '@app/models/branch';
import { Loan } from '@app/models/loan';
import { Constants } from "@app/constants/constants";
import { GroupService } from "@app/services/group.service";
import { TranslateService } from '@ngx-translate/core';
import { LoanService } from '@app/services/loan.service';

@Component({
    selector: 'app-loan-detail-summary',
    templateUrl: './loan-detail-summary.component.html',
    styleUrls: ['./loan-detail-summary.component.css'],
    standalone: false
})
export class LoanDetailSummaryComponent implements OnInit, OnChanges {

  @Input() loan: Loan;
  @Input() isLoanApproval: boolean;
  @Input() customerGroupId: string;
  @Input() branches: Array<Branch>;

  notesHierarchy: any[] = [];
  interestCalculationOptions = Constants.INTEREST_CALCULATION_OPTIONS;

  constructor(
    private customerGroupService: GroupService,
    private loanService: LoanService,
    private translate: TranslateService
    ) { }

  ngOnInit(): void {
    if (!this.loan) {
      this.loan = new Loan();
    }else{
      if(!this.customerGroupId) {
        this.getCustomerGroupInfo(this.loan.customerGroupId)
      }
    }
  }

  ngOnChanges() {
    this.buildNotesHierarchy(this.loan?.notes);
  }

  formatNumber(value) {
    if (value === null || value === undefined || isNaN(value)) return '';
    return MethodsHelper.numberWithCommas(value);
  }

  getFormatDate(yyyymmdd) {
    if (yyyymmdd) {
      let items = yyyymmdd.split("-");
      if (items.length == 3) {
        return items[2] + "." + items[1] + "." + items[0].substr(2);
      }
    }
    return "";
  }

  findSymbol() {
    if (this.loan && this.loan.currency) {
      return MethodsHelper.findSymbol(this.loan.currency);
    }
    return "";
  }

  formatGracePeriod(value) {
    if (value) {
      let graceNumber = value.substring(1, value.length - 1);
      if (value[length - 1] == 'D') {
        return graceNumber + " days";
      } else if (value[length - 1] == 'Y') {
        return graceNumber + " years";
      }
      return graceNumber + " months";
    }
    return "";
  }

    findInterestCalculationOptions(type: string): string {
        const itemType = this.interestCalculationOptions.find(p => p.value === type);
        if (itemType) {
            return this.translate.instant(itemType.label);
        }
        return '';
    }


    loanPeriodGenType(type) {
    let typePeriod = '';
    switch (type) {
      case "M": typePeriod = "months";
        break;
      case "Y": typePeriod = "years";
        break;
      case "W": typePeriod = "weeks";
        break;
      case "D": typePeriod = 'days';
        break;
      default: typePeriod = 'unknown'
    }
    return typePeriod;
  }

  getLoanPeriodGen() {
    if (this.loan && this.loan.period) {
      let typePeriod = this.loanPeriodGenType(this.loan.period[this.loan.period.length - 1]);
      return this.loan.period.substring(1, this.loan.period.length - 2) + " " + typePeriod;
    }
    return "";
  }

  loanPeriodCovertToString(){
    let period = '';
    if (this.loan && this.loan.period) {
      let lastChar = this.loan.period.charAt(this.loan.period.length-1);
      period = this.loan.period.substring(1);
      let lastPart = '';
      switch (lastChar){
        case "M":
          lastPart = "Month(s)";
          break;
        case "Y":
          lastPart = "Year(s)";
          break;
        case "D":
          lastPart = "Day(s)";
          break;
        case "W":
          lastPart = "Week(s)";
          break;
        default:
          break;
      }
      period = period.slice(0,-1) +" " +lastPart;
    }
    return period;
  }

  getBranchName(branchId) {
    if (this.branches) {
      let item = this.branches.find(p => p.id == branchId);
      if (item) {
        return item.name;
      }
    }
    return "";
  }

  getBranchNameFromCode(branchCode) {
    if (this.branches) {
      let item = this.branches.find(p => p.code == branchCode);
      if (item) {
        return item.name;
      }
    }
    return "";
  }

  getUpfrontFee(){
    return MethodsHelper.getUpfrontFee(this.loan);    
  }

  getUpfrontServiceFee() {
    return MethodsHelper.getUpfrontServiceFee(this.loan);
  }

  getLoanServiceFeeAmount(){
    return MethodsHelper.getLoanServiceFeeAmount(this.loan);
  }

  getLatePaymentFeePrincipal() {
    return MethodsHelper.getLatePaymentFeePrincipal(this.loan);
  }

  getLatePaymentFeeInterest() {
    return MethodsHelper.getLatePaymentFeeInterest(this.loan);
  }

  getLatePaymentFeeServicefee() {
    return MethodsHelper.getLatePaymentFeeServicefee(this.loan);
  }

  getCustomerGroupInfo(customerGroupId: string | undefined) {
    if(customerGroupId){
      this.customerGroupService.getById(customerGroupId).subscribe(data => {
        if(data){
          this.customerGroupId = data.groupId
        }
      });
    }
  }

  buildNotesHierarchy(data: any[]) {
    if (!data) return;

    const hierarchyMap = new Map<string, { parent: string, children: string[] }>();

    data.forEach(item => {
        if (item.parentId) {
            const parent = data.find(parentItem => parentItem.id === item.parentId);
            if (parent) {
                if (!hierarchyMap.has(parent.id)) {
                    hierarchyMap.set(parent.id, { parent: parent.name, children: [] });
                }
                hierarchyMap.get(parent.id)?.children.push(item.name);
            }
        } else {
            if (!hierarchyMap.has(item.id)) {
                hierarchyMap.set(item.id, { parent: item.name, children: [] });
            }
        }
    });

    this.notesHierarchy = Array.from(hierarchyMap.values()).map(({ parent, children }) => ({
        parent,
        child: children.length ? children.join(', ') : null
    }));
  }

  showAttachedFiles() {
    if (!this.loan?.files || this.loan.files.length === 0) return '';
    const allTitles = this.loan.files.map((f: any) => f.title || f.name).filter(t => t && t.trim() !== "");
    return Array.from(new Set(allTitles)).join(', ');
  }

  downloadFile(file: any) {
    console.log("file: ", file);
    if (file && file.id && this.loan && this.loan.id) {
      this.loanService.getFileById(file.id, this.loan.id).subscribe((res: any) => {
        if (res) {
          const blob = new Blob([res], { type: res.type });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = file.title || file.name || 'download';
          link.click();
          window.URL.revokeObjectURL(url);
        }
      });
    }
  }

  formatUppercaseText(str: string | null | undefined) {
    return MethodsHelper.textCapitalize(str);
  }

  formatTextCapitalize(str: string) {
    if (!str) return '';
    if (str.indexOf('_') !== -1) {
      str = str.split('_').join(' ');
    }
    return MethodsHelper.textCapitalize(str);
  }
}
